import { currentSubSession, setCurrentSubSession } from './currentSubSession';
import type { SubSessionClaim } from './subSessionClaim';
import { announceSubSessionId, withdrawSubSessionId } from './subSessionEvents';
import type { SubSessionStorage } from './subSessionStorage';
import { clearSubSessionId, readSubSessionId, writeSubSessionId } from './subSessionStorage';
import { readSubSessionIdFromSearch } from './subSessionUrl';
import {
  isWorkerSubSessionEvent,
  reportSubSessionAssigned,
  reportSubSessionClaimKept,
  reportSubSessionEvent,
  reportSubSessionTrusted,
  reportSubSessionTrustWithdrawn,
} from './telemetry';
import type { SubSessionEventData } from './telemetry';
import { NEW_SUB_SESSION_ID, WORKER_MESSAGE } from './wireContract';

export type MessageContainer = Pick<
  ServiceWorkerContainer,
  'addEventListener' | 'controller' | 'startMessages'
>;

export type SubSessionBridge = {
  /** Claims this tab's id again, e.g. after a back/forward cache restore. */
  reclaim: () => Promise<void>;
};

type WorkerMessageData = {
  subSessionId?: unknown;
  type?: unknown;
  name?: unknown;
  data?: unknown;
  pathname?: unknown;
};

const isRecord = (value: unknown): value is SubSessionEventData =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Answers on the port so the worker does not ask another tab to report the same event.
const reportWorkerEvent = (data: WorkerMessageData, port: MessagePort | undefined): void => {
  const isReported =
    isWorkerSubSessionEvent(data.name) &&
    reportSubSessionEvent(
      data.name,
      isRecord(data.data) ? data.data : {},
      typeof data.pathname === 'string' ? data.pathname : undefined,
    );
  port?.postMessage({ isReported });
};

const isUsableId = (value: unknown): value is string =>
  typeof value === 'string' && value !== '' && value !== NEW_SUB_SESSION_ID;

const firstUsableId = (...candidates: Array<string | null>): string | null =>
  candidates.find(isUsableId) ?? null;

/**
 * A navigation cannot carry the header, so an embedded id on a document without
 * `ibe.ss` was minted for this document and no other tab can hold it. Duplicated
 * tabs and copied links always carry `ibe.ss` and wait for the claim.
 */
const idMintedForThisDocument = (
  serverResolvedSubSessionId: string | null,
  search: string,
): string | null =>
  isUsableId(serverResolvedSubSessionId) && readSubSessionIdFromSearch(search) === null
    ? serverResolvedSubSessionId
    : null;

/**
 * Lets the service worker read and write this tab's sub-session id. Install it
 * before the app makes requests; asks sent earlier are queued by the browser
 * until `startMessages()`.
 */
export const installSubSessionBridge = ({
  claim,
  container,
  search,
  serverResolvedSubSessionId,
  storage,
}: {
  claim: SubSessionClaim;
  container: MessageContainer;
  search: string;
  serverResolvedSubSessionId: string | null;
  storage: SubSessionStorage;
}): SubSessionBridge => {
  // The worker asks again after an idle shutdown, but the claim runs once per document.
  let resolution: Promise<string | null> | null = null;
  // Bumped whenever the id is decided otherwise, so a late claim result cannot overwrite it.
  let generation = 0;

  const adopt = (subSessionId: string): void => {
    writeSubSessionId(storage, subSessionId);
    setCurrentSubSession(subSessionId);
    announceSubSessionId(subSessionId);
  };

  const giveUp = (subSessionId: string): void => {
    clearSubSessionId(storage);
    withdrawSubSessionId(subSessionId);
  };

  // Uses the id at once and claims it in the background as a safety net.
  const trust = (subSessionId: string): void => {
    generation += 1;
    const trustGeneration = generation;
    adopt(subSessionId);
    reportSubSessionTrusted();
    resolution = Promise.resolve(subSessionId);

    claim
      .claim(subSessionId)
      .then((mayKeep) => {
        if (mayKeep || trustGeneration !== generation) {
          return;
        }
        generation += 1;
        setCurrentSubSession(null);
        giveUp(subSessionId);
        resolution = Promise.resolve(null);
        reportSubSessionTrustWithdrawn();
        container.controller?.postMessage({ type: WORKER_MESSAGE.RESET });
      })
      .catch(() => undefined);
  };

  const runClaim = (candidateId: string): Promise<string | null> => {
    generation += 1;
    const claimGeneration = generation;
    // Unknown until the other tabs had their chance to object.
    setCurrentSubSession(null);

    return claim.claim(candidateId).then((mayKeep) => {
      if (claimGeneration !== generation) {
        // Whatever decided the id since owns the answer; the current id may still be pending.
        return resolution ?? currentSubSession();
      }
      if (mayKeep) {
        reportSubSessionClaimKept({
          isFromLinkOnly:
            readSubSessionIdFromSearch(search) === candidateId &&
            readSubSessionId(storage) !== candidateId,
        });
        adopt(candidateId);
        return candidateId;
      }
      giveUp(candidateId);
      return null;
    });
  };

  const resolveOwnSubSessionId = (): Promise<string | null> => {
    if (!resolution) {
      // Claimed before use, so a copied link is caught like a duplicated tab.
      const candidateId = firstUsableId(
        serverResolvedSubSessionId,
        readSubSessionId(storage),
        readSubSessionIdFromSearch(search),
      );
      resolution = candidateId ? runClaim(candidateId) : Promise.resolve(null);
    }

    return resolution;
  };

  container.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as WorkerMessageData | null;
    if (!data) {
      return;
    }

    if (data.type === WORKER_MESSAGE.WHO_ARE_YOU) {
      const port = event.ports?.[0];
      if (port) {
        resolveOwnSubSessionId()
          .then((subSessionId) => port.postMessage({ subSessionId }))
          .catch(() => port.postMessage({ subSessionId: null }));
      }
      return;
    }

    if (data.type === WORKER_MESSAGE.TELEMETRY) {
      reportWorkerEvent(data, event.ports?.[0]);
      return;
    }

    if (data.type === WORKER_MESSAGE.ASSIGNED && isUsableId(data.subSessionId)) {
      const { subSessionId } = data;
      generation += 1;
      reportSubSessionAssigned(claim.owned(), subSessionId);
      claim.own(subSessionId);
      adopt(subSessionId);
      resolution = Promise.resolve(subSessionId);
    }
  });

  // Announced as well as answered: the worker's ask can time out while a large
  // bundle is still evaluating, and it would then treat this page as legacy.
  const announceReady = (): void => {
    resolveOwnSubSessionId()
      .then((subSessionId) => {
        container.controller?.postMessage({ type: WORKER_MESSAGE.READY, subSessionId });
      })
      .catch(() => undefined);
  };

  // On a first visit the worker takes control part-way through the load.
  container.addEventListener('controllerchange', announceReady);

  // Before startMessages, so even an ask queued during load gets the trusted id.
  const trustedId = idMintedForThisDocument(serverResolvedSubSessionId, search);
  if (trustedId) {
    trust(trustedId);
  }

  container.startMessages();
  announceReady();

  return {
    reclaim: () => {
      const ownedId = claim.owned();
      if (!ownedId) {
        return Promise.resolve();
      }

      const reclaimed = runClaim(ownedId);
      resolution = reclaimed;

      return reclaimed
        .then((subSessionId) => {
          if (!subSessionId) {
            container.controller?.postMessage({ type: WORKER_MESSAGE.RESET });
          }
        })
        .catch(() => undefined);
    },
  };
};
